import { useCallback, useEffect, useRef, useState } from 'react';
import { UpdateToEventBatchBootstrapView, UpdateToEventQueryResult, QueryResultIdentifier } from '@mark43/cad-api';
import useUpdateToEventResource from './use-update-to-event-resource';

export type BulkBootstrapResult = {
    data: Map<string, UpdateToEventQueryResult & { eventAutocompleteView: UpdateToEventBatchBootstrapView['eventAutocompleteView'] }>;
    isLoading: boolean;
    isError: boolean;
};

export const useUpdateToEventBulkBootstrap = ({
    eventId,
}: {
    eventId?: number;
}): {
    registerIdentifier: (identifier: QueryResultIdentifier) => void;
    result: BulkBootstrapResult;
    refetch: () => void;
} => {
    const { getUpdateToEventBatchBootstrap } = useUpdateToEventResource();

    const pendingIdentifiersRef = useRef<Map<string, QueryResultIdentifier>>(new Map());
    const [identifiers, setIdentifiers] = useState<QueryResultIdentifier[]>([]);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [resultMap, setResultMap] = useState<BulkBootstrapResult['data']>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current !== null) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const registerIdentifier = useCallback((identifier: QueryResultIdentifier) => {
        const key = identifier.queryResultId ?? identifier.queryId;
        if (pendingIdentifiersRef.current.has(key)) {
            return;
        }
        pendingIdentifiersRef.current.set(key, identifier);

        if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            setIdentifiers(Array.from(pendingIdentifiersRef.current.values()));
            debounceTimerRef.current = null;
        }, 50);
    }, []);

    const fetchBatch = useCallback(
        async (ids: QueryResultIdentifier[]) => {
            if (ids.length === 0) {
                return;
            }
            setIsLoading(true);
            setIsError(false);
            try {
                const result = await getUpdateToEventBatchBootstrap({
                    eventId,
                    queryResultIdentifiers: ids,
                });
                const batchView: UpdateToEventBatchBootstrapView | undefined = result.data?.data;
                if (!batchView) {
                    return;
                }
                const map: BulkBootstrapResult['data'] = new Map();
                for (const queryResult of batchView.updateToEventQueryResults) {
                    const mapKey = queryResult.queryResultId ?? queryResult.queryId;
                    map.set(mapKey, { ...queryResult, eventAutocompleteView: batchView.eventAutocompleteView });
                }
                setResultMap(map);
            } catch {
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [eventId]
    );

    useEffect(() => {
        if (identifiers.length > 0) {
            fetchBatch(identifiers);
        }
    }, [identifiers, fetchBatch]);

    const refetch = useCallback(() => {
        fetchBatch(identifiers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identifiers, fetchBatch]);

    return {
        registerIdentifier,
        result: { data: resultMap, isLoading, isError },
        refetch,
    };
};
